import React, { useState, useEffect } from 'react';
import { Button, Modal, Typography, Space } from 'antd';
import { DownloadOutlined, CloseOutlined, MobileOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { trackEvent } from '../../utils/analytics';

const { Title, Paragraph, Text } = Typography;

// Theme colors to match the app
const THEME = {
  primary: '#2e186a',
  secondary: '#ff825c',
};

// Styled components for themed buttons
const StyledPrimaryButton = styled(Button)`
  background-color: ${THEME.primary};
  border-color: ${THEME.primary};
  color: white;
  
  &:hover, &:focus {
    background-color: ${THEME.secondary};
    border-color: ${THEME.secondary};
    color: white;
  }
  
  &:active {
    background-color: ${THEME.primary};
    border-color: ${THEME.primary};
  }
`;

const StyledLinkButton = styled(Button)`
  color: ${THEME.primary};
  
  &:hover, &:focus {
    color: ${THEME.secondary};
  }
`;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if it's iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         (window.navigator as any).standalone || 
                         document.referrer.includes('android-app://');
    
    // Don't show prompt if already installed
    if (isStandalone) {
      return;
    }

    // Store the install prompt event for later use
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Show the modal after a short delay to not interrupt initial user experience
      setTimeout(() => {
        // Check if user has previously dismissed the prompt (stored in localStorage)
        const lastPrompt = localStorage.getItem('installPromptDismissed');
        const lastPromptTime = lastPrompt ? new Date(lastPrompt).getTime() : 0;
        const now = new Date().getTime();
        
        // Only show again if it's been at least 7 days since last dismissal
        if (!lastPrompt || (now - lastPromptTime) > 7 * 24 * 60 * 60 * 1000) {
          setShowModal(true);
        }
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt && !isIOS) {
      return;
    }

    setShowModal(false);

    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        trackEvent('install_prompt_accepted');
      } else {
        trackEvent('install_prompt_dismissed');
        // Store dismissal time
        localStorage.setItem('installPromptDismissed', new Date().toISOString());
      }
      
      // Clear the saved prompt since it can't be used again
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowModal(false);
    // Store dismissal time
    localStorage.setItem('installPromptDismissed', new Date().toISOString());
  };

  if (!showModal) {
    return null;
  }

  return (
    <Modal
      open={showModal}
      onCancel={handleDismiss}
      footer={null}
      closable={true}
      closeIcon={<CloseOutlined />}
      width={350}
    >
      <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }}>
        <MobileOutlined style={{ fontSize: 48, color: THEME.primary, marginBottom: 16 }} />
        
        <Title level={4}>Install Nijulishe App</Title>
        
        <Paragraph>
          Install our app for a better experience with faster loading times and offline access to outage information.
        </Paragraph>
        
        {isIOS ? (
          <div>
            <Paragraph>
              <Text strong>To install on iOS:</Text>
            </Paragraph>
            <Paragraph>
              1. Tap <Text code>Share</Text> icon at the bottom of your screen
            </Paragraph>
            <Paragraph>
              2. Scroll and select <Text code>Add to Home Screen</Text>
            </Paragraph>
            <Paragraph>
              3. Tap <Text code>Add</Text> in the top right corner
            </Paragraph>
            <StyledPrimaryButton 
              onClick={handleDismiss}
              style={{ marginTop: 16 }}
            >
              Got it
            </StyledPrimaryButton>
          </div>
        ) : (
          <StyledPrimaryButton 
            icon={<DownloadOutlined />} 
            onClick={handleInstallClick}
            style={{ marginTop: 16 }}
          >
            Install Now
          </StyledPrimaryButton>
        )}
        
        <StyledLinkButton 
          type="link" 
          onClick={handleDismiss}
          style={{ marginTop: 8 }}
        >
          Maybe Later
        </StyledLinkButton>
      </Space>
    </Modal>
  );
};

export default InstallPrompt; 